<template>
  <div class="hash">
    <Header />
    <div class="box-bg-2 search-input border-bg d-flex ai-center result">
      <input
        class="text-gray-1 flex-1 box-bg-2"
        type="text-gray-1"
        :placeholder="$t('placeholder')"
        v-model="hash"
        @keydown.enter="getTransitDetails"
      />
      <img
        class="delete"
        :src="deleteImg"
        v-if="hash !== ''"
        @click="hash = ''"
        style=""
      />
      <div
        class="button search-button-bg d-flex ai-center jc-center"
        @click="getTransitDetails"
      >
        <img class="loading" src="../assets/loading.png" v-if="isLoading" />
        <img src="../assets/search.png" v-else />
      </div>
    </div>
    <div class="box-bg-2 result-content" v-if="sourceHash && !isError">
      <div class="text-gray-1 title border-bottom-bg">
        {{ $t('transDetails') }}
      </div>
      <!-- status -->
      <div class="row">
        <div class="text-gray-1 name">{{ $t('status') }}</div>
        <div class="text-gray-1 value d-flex ai-center">
          <div
            class="status-value d-flex ai-center"
            :class="{
              success: status === 'Success',
              failure: status === 'Failure',
              pending: status === 'Receiving',
            }"
          >
            <template v-if="status === 'Success'">
              <img class="status-img" src="../assets/success.png" />
              <span>{{ $t('statusSuccess') }}</span>
            </template>
            <template v-if="status === 'Failure'">
              <img class="status-img" src="../assets/failure.png" />
              <span>{{ $t('statusFailure') }}</span>
            </template>
            <template v-if="status === 'Receiving'">
              <img class="status-img" src="../assets/ongoing.png" />
              <span>{{ $t('onGoing') }}</span>
            </template>
          </div>
          <span
            v-if="status !== 'Success' && statusNotesText"
            class="text-gray-3"
            style="margin-left: 0.05rem"
          >
            ({{ statusNotesText }})
          </span>
        </div>
      </div>
      <!-- 日期 -->
      <div class="row">
        <div class="text-gray-1 name">{{ $t('date') }}</div>
        <div class="text-gray-1 value date-value d-flex ai-center">
          <div class="before-time d-flex ai-center">
            <img class="time" src="../assets/time-dark.png" v-if="isDark" />
            <img class="time" src="../assets/time.png" v-else />
            <span>{{ beforeTime }}</span>
          </div>
          <span class="text-gray-3">({{ timestamp }} +UTC)</span>
        </div>
      </div>
      <div class="row line"></div>
      <template v-if="!isSameChain">
        <div class="row">
          <div class="text-gray-1 name">{{ $t('sourceChain') }}</div>
          <div class="text-gray-1 value">{{ fromChainTitle }}</div>
        </div>
        <div class="row">
          <div class="text-gray-1 name">{{ $t('destChain') }}</div>
          <div class="text-gray-1 value">{{ toChainTitle }}</div>
        </div>
      </template>
      <div class="row" v-else>
        <div class="text-gray-1 name">{{ $t('chain') }}</div>
        <div class="text-gray-1 value">{{ fromChainTitle }}</div>
      </div>
      <!-- 跨链的两个 hash -->
      <template v-if="!isSameChain">
        <div class="row column">
          <div class="text-gray-1 name">{{ $t('sourceHash') }}</div>
          <div class="text-gray-1 value right-content">
            <span @click="goExplorer(sourceExplorer)">{{ sourceHash }}</span>
            <div class="copy-wrap" v-if="!copiedList[0].isCopied">
              <img
                class="copy-img"
                :src="copyImg"
                @click="onCopy(sourceHash, 0)"
              />
              <div class="copy-text">
                {{ $t('copySourceHash') }}
                <div class="triangle"></div>
              </div>
            </div>
            <div class="d-flex ai-center" v-else>
              <img class="copy-img" :src="copiedImg" />
              <span class="text-gray-3">{{ $t('copied') }}</span>
            </div>
            <img
              class="copy-img-m"
              src="../assets/copy-hover.png"
              @click="onCopyMobile(sourceHash)"
            />
          </div>
        </div>
        <div class="row column" v-if="targetHash">
          <div class="text-gray-1 name">{{ $t('destHash') }}</div>
          <div class="text-gray-1 value right-content">
            <span @click="goExplorer(targetExplorer)">{{ targetHash }}</span>
            <div class="copy-wrap" v-if="!copiedList[1].isCopied">
              <img
                class="copy-img"
                :src="copyImg"
                @click="onCopy(targetHash, 1)"
              />
              <div class="copy-text">
                {{ $t('copyDestHash') }}
                <div class="triangle"></div>
              </div>
            </div>
            <div class="d-flex ai-center" v-else>
              <img class="copy-img" :src="copiedImg" />
              <span class="text-gray-3">{{ $t('copied') }}</span>
            </div>
            <img
              class="copy-img-m"
              src="../assets/copy-hover.png"
              @click="onCopyMobile(targetHash)"
            />
          </div>
        </div>
      </template>
      <!-- 同链 -->
      <div class="row column" v-else>
        <div class="text-gray-1 name">{{ $t('hash') }}</div>
        <div class="text-gray-1 value right-content">
          <span @click="goExplorer(sourceExplorer)">{{ sourceHash }}</span>
          <div class="copy-wrap" v-if="!copiedList[0].isCopied">
            <img
              class="copy-img"
              :src="copyImg"
              @click="onCopy(sourceHash, 0)"
            />
            <div class="copy-text">
              {{ $t('copyHash') }}
              <div class="triangle"></div>
            </div>
          </div>
          <div class="d-flex ai-center" v-else>
            <img class="copy-img" :src="copiedImg" />
            <span class="text-gray-3">{{ $t('copied') }}</span>
          </div>
          <img
            class="copy-img-m"
            src="../assets/copy-hover.png"
            @click="onCopyMobile(sourceHash)"
          />
        </div>
      </div>
      <div class="row column">
        <div class="text-gray-1 name">{{ $t('fromAddress') }}</div>
        <div class="text-gray-1 value right-content">
          <span @click="goExplorer(fromAddress, fromChain)">{{
            fromAddress
          }}</span>
          <div class="copy-wrap" v-if="!copiedList[2].isCopied">
            <img
              class="copy-img"
              :src="copyImg"
              @click="onCopy(fromAddress, 2)"
            />
            <div class="copy-text">
              {{ $t('copySourceAddress') }}
              <div class="triangle"></div>
            </div>
          </div>
          <div class="d-flex ai-center" v-else>
            <img class="copy-img" :src="copiedImg" />
            <span class="text-gray-3">{{ $t('copied') }}</span>
          </div>
          <img
            class="copy-img-m"
            src="../assets/copy-hover.png"
            @click="onCopyMobile(fromAddress)"
          />
        </div>
      </div>
      <div class="row column" v-if="toAddress">
        <div class="text-gray-1 name">{{ $t('toAddress') }}</div>
        <div class="text-gray-1 value right-content">
          <span @click="goExplorer(toAddress, toChain)">{{ toAddress }}</span>
          <div class="copy-wrap" v-if="!copiedList[3].isCopied">
            <img
              class="copy-img"
              :src="copyImg"
              @click="onCopy(toAddress, 3)"
            />
            <div class="copy-text">
              {{ $t('copyDestAddress') }}
              <div class="triangle"></div>
            </div>
          </div>
          <div class="d-flex ai-center" v-else>
            <img class="copy-img" :src="copiedImg" />
            <span class="text-gray-3">{{ $t('copied') }}</span>
          </div>
          <img
            class="copy-img-m"
            src="../assets/copy-hover.png"
            @click="onCopyMobile(toAddress)"
          />
        </div>
      </div>
      <div class="row line"></div>
      <div class="row column">
        <div class="text-gray-1 name">{{ $t('sendValue') }}</div>
        <div class="value right-content">
          <span class="text-gray-1 send-value">
            {{ amountIn + ' ' + fromSymbol }}
          </span>
          <div class="copy-wrap" v-if="isSecuritySend">
            <img
              class="copy-img"
              :src="securityImg"
              @click="goSecurity(fromToken, fromChain)"
            />
            <div class="copy-text">
              {{ $t('securityText') }}
              <div class="triangle"></div>
            </div>
          </div>
          <img
            v-if="isSecuritySend"
            class="copy-img-m"
            src="../assets/security-hover.png"
            @click="goSecurity(fromToken, fromChain)"
          />
        </div>
      </div>
      <div class="row column">
        <div class="text-gray-1 name">{{ $t('receiveValue') }}</div>
        <div class="value right-content">
          <span class="status-value receive-value success">
            {{ amountOut + ' ' + toSymbol }}
          </span>
          <div class="copy-wrap" v-if="isSecurityReceive">
            <img
              class="copy-img"
              :src="securityImg"
              @click="goSecurity(toToken, toChain)"
            />
            <div class="copy-text">
              {{ $t('securityText') }}
              <div class="triangle"></div>
            </div>
          </div>
          <img
            v-if="isSecurityReceive"
            class="copy-img-m"
            src="../assets/security-hover.png"
            @click="goSecurity(toToken, toChain)"
          />
        </div>
        <div class="wallet-content" v-if="isCCTP && receiveData">
          <div
            class="button receive-btn"
            :class="{ gray: status === 'Success' }"
            @click="onReceive"
          >
            {{ status === 'Success' ? $t('received') : $t('receive') }}
          </div>
          <div
            class="button wallet-btn wallet-pc-btn gray"
            @click="connectWallet"
            v-if="connected.account"
          >
            {{ connected.account }}
          </div>
          <div
            class="button wallet-btn wallet-mobile-btn gray"
            @click="connectWallet"
            v-if="connected.account"
          >
            {{
              connected.account.slice(0, 12) +
              '...' +
              connected.account.slice(-6)
            }}
          </div>
          <div class="button wallet-btn" @click="connectWallet" v-else>
            {{ $t('connectWallet') }}
          </div>
        </div>
      </div>
      <div class="row line"></div>
      <div class="row dexs-wrap" v-if="dexs.length">
        <div class="text-gray-1 name">{{ $t('liquidityProvider') }}</div>
        <div class="text-gray-1 value d-flex ai-center flex-wrap">
          <div
            class="dexs d-flex ai-center"
            v-for="(item, index) in dexs"
            :class="{ 'more-dex': dexs.length >= 1 && item.dex }"
            :key="index"
          >
            <template v-if="item.icon || item.dex">
              <img v-if="item.icon" :src="item.icon" />
              <img v-else src="../assets/transit.png" />
              <span class="text-gray-3">{{ item.dex }}</span>
            </template>
          </div>
          <div
            class="text-grey-1 question"
            :class="{ isDexs: !dexs.length }"
            @click="goQuestion"
          >
            {{ $t('question') }}
          </div>
        </div>
      </div>
    </div>
    <div
      class="box-bg-2 result-content error-content d-flex flex-column ai-center jc-center"
      v-if="isError"
    >
      <img src="../assets/empty-dark.png" v-if="isDark" />
      <img src="../assets/empty.png" v-else />
      <span class="text-gray-3">{{ $t('errorMsg') }}</span>
    </div>
    <Toast v-if="isToast" @closeToast="closeToast" />
    <Modal v-if="show" @close="close" :url="questionUrl" />
  </div>
</template>

<script>
import _ from 'lodash';
import dayjs from 'dayjs';
import { mapGetters } from 'vuex';
import tp from 'tp-js-sdk';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import Header from '../components/Header.vue';
import Toast from '../components/Toast.vue';
import Modal from '../components/Modal.vue';
import {
  explorerMap,
  chainIdMap,
  statusNotesMap,
  chainIdMainnetMap,
} from '../utils';
import { AGGR_DOMAIN } from '../utils/config';
import { CHAIN_INFO_MAP } from '../utils/constants';

const dexMap = {
  METAPATH: 'https://t.me/swftswap_CN',
  ANYSWAP: 'https://t.me/multi_cn',
  CBRIDGE: 'https://t.me/celernetwork',
};

export default {
  name: 'hash',
  components: { Header, Toast, Modal },
  data() {
    return {
      connected: {
        account: '',
        chainId: '',
      },
      hash: '',
      sourceHash: '',
      targetHash: '',
      status: '',
      statusNotes: '',
      fromToken: '',
      toToken: '',
      amountIn: '',
      amountOut: '',
      fromAddress: '',
      toAddress: '',
      fromChain: '',
      fromChainTitle: '',
      toChainTitle: '',
      toChain: '',
      timestamp: '',
      fromSymbol: '',
      toSymbol: '',
      sourceExplorer: '',
      targetExplorer: '',
      timestampNum: '',
      beforeTime: '',
      dexs: [],
      isLoading: false,
      isError: false,
      isCopied: false,
      copiedList: [
        { isCopied: false },
        { isCopied: false },
        { isCopied: false },
        { isCopied: false },
      ],
      isToast: false,
      show: false,
      questionUrl: '',
      receiveData: null,
      isTP: false,
    };
  },

  computed: {
    ...mapGetters(['isDark']),
    statusNotesText() {
      if (this.statusNotes === 'BigAmount(Wait24hours)') {
        return this.$t('BigAmount');
      } else if (this.statusNotes === 'ExceedLimit(LessThenMinAmount)') {
        return this.$t('ExceedLimit');
      } else {
        return this.$t(`${this.statusNotes}`);
      }
    },
    copyImg() {
      let str = '';
      this.isDark ? (str = '-dark') : '';
      return require(`../assets/copy${str}.png`);
    },
    copiedImg() {
      let str = '';
      this.isDark ? (str = '-dark') : '';
      return require(`../assets/copied${str}.png`);
    },
    deleteImg() {
      let str = '';
      this.isDark ? (str = '-dark') : '';
      return require(`../assets/delete${str}.png`);
    },
    isSecuritySend() {
      if (chainIdMap[this.fromChain]) {
        return true;
      } else {
        return false;
      }
    },
    isSecurityReceive() {
      if (chainIdMap[this.toChain]) {
        return true;
      } else {
        return false;
      }
    },
    isSameChain() {
      if (this.fromChain == this.toChain) {
        return true;
      } else {
        return false;
      }
    },
    securityImg() {
      let str = '';
      this.isDark ? (str = '-dark') : '';
      return require(`../assets/security${str}.png`);
    },
  },
  created() {
    const { hash } = this.$route.params;
    // console.log('hash', hash)
    if (hash) {
      this.hash = hash;
      this.getTransitDetails();
    }
  },

  methods: {
    /** 查询CCTP跨链数据 */
    async getCCTPReceiveData(bridge, fromChainID, toChainID, hash) {
      const data = { bridge, fromChainID, toChainID, hash };
      try {
        const response = await fetch(
          `${AGGR_DOMAIN}/v3/cross/receive?bridge=${bridge}&fromChainID=${fromChainID}&toChainID=${toChainID}&hash=${hash}`
        );
        const res = await response.json();
        if (res.result === 0 && res.data) {
          // console.log('res', res);
          this.receiveData = res.data;
        }
      } catch (error) {
        console.log(error);
      }
    },
    connectWallet() {
      if (navigator.userAgent.indexOf('TokenPocket') > -1) {
        this.isTP = true;
        if (window.ethereum) {
          this.connected.account = window.ethereum.selectedAddress;
          this.connected.chainId = parseInt(window.ethereum.chainId, 16);
        } else {
          tp.getWallet({
            walletTypes: [chainIdMainnetMap[this.toChain].toString()],
            switch: true,
          }).then(() => location.reload());
        }
      } else {
        window.ethereum
          .request({
            method: 'eth_requestAccounts',
          })
          .then((res) => {
            this.connected.account = res[0];
            this.connected.chainId = parseInt(window.ethereum.chainId, 16);
            window.ethereum.on('accountsChanged', (accounts) => {
              this.connected.account = accounts[0];
            });
            window.ethereum.on('chainChanged', (chainId) => {
              this.connected.chainId = parseInt(chainId, 16);
            });
          });
      }
    },
    switchChain(type) {
      console.log('type', type);
      // 尝试切换
      window.ethereum
        .request({
          method: 'wallet_addEthereumChain',
          params: CHAIN_INFO_MAP[type].CHAIN_PARAMS,
        })
        .then((res) => {
          console.log('切换网络成功');
          if (this.isTP) {
            location.reload();
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    onReceive() {
      if (this.status === 'Success') {
        return;
      }
      if (
        this.receiveData &&
        this.connected.chainId !== this.receiveData.chainId
      ) {
        this.switchChain(this.toChain);
      } else {
        let param = {
          from: this.connected.account,
          to: this.receiveData.contract,
          data: this.receiveData.data,
        };
        window.ethereum
          .request({
            method: 'eth_sendTransaction',
            params: [param],
          })
          .then((hash) => {
            console.log('领取成功');
          })
          .catch((err) => {
            console.log(err);
          });
      }
    },
    goQuestion() {
      if (this.isSameChain) {
        this.show = true;
        this.questionUrl = 'https://t.me/Transit_Finance';
      } else {
        let dex = this.dexs[0];
        if (dex && dex.dex) {
          window.open(dexMap[dex.dex]);
        }
      }
    },
    close() {
      this.show = false;
    },
    closeToast() {
      this.isToast = false;
    },
    onCopy(text, index) {
      this.$copyText(text);
      if (!this.copiedList[index].isCopied) {
        this.copiedList[index].isCopied = true;
        setTimeout(() => {
          this.copiedList[index].isCopied = false;
        }, 1000);
      }
    },
    onCopyMobile(text) {
      this.$copyText(text);
      if (!this.isToast) {
        this.isToast = true;
        setTimeout(() => {
          this.isToast = false;
        }, 1000);
      }
    },
    goSecurity(address, chain) {
      window.open(
        `https://tokensecurity.tokenpocket.pro/?locale=${this.$i18n.locale}&utm_source=tokenpocket#/?address=${address}&blockchain_id=${chainIdMap[chain]}`
      );
    },
    goExplorer(address, chain) {
      // console.log('address', address)
      if (chain) {
        window.open(explorerMap[chain] + address);
      } else {
        // console.log('sourceExplorer', this.sourceExplorer)
        window.open(address);
      }
    },
    async getTransitDetails() {
      if (!this.hash) {
        return;
      }
      this.$router.replace({
        path: `/hash/${encodeURIComponent(this.hash)}`,
      });
      this.isLoading = true;
      // this.isError = false
      try {
        const response = await fetch(
          `${AGGR_DOMAIN}/v3/transit/details?hash=${this.hash}`
        );
        const res = await response.json();
        if (res.result === 0) {
          // console.log('data', res.data);
          const {
            sourceHash,
            targetHash,
            status,
            statusNotes,
            fromToken,
            toToken,
            amountIn = 0,
            amountOut = 0,
            toDecimals,
            fromAddress,
            toAddress,
            fromChain,
            fromChainTitle,
            toChainTitle,
            toChain,
            timestamp,
            fromSymbol,
            toSymbol,
            sourceExplorer,
            targetExplorer,
            fromDecimals,
            dexs,
          } = res.data;
          this.sourceHash = sourceHash;
          this.targetHash = targetHash;
          this.status = status;
          this.statusNotes = statusNotes;
          this.fromToken = fromToken;
          this.toToken = toToken;
          this.fromAddress = fromAddress;
          this.toAddress = toAddress;
          this.fromChain = fromChain;
          this.fromChainTitle = fromChainTitle;
          this.toChainTitle = toChainTitle;
          this.toChain = toChain;
          this.fromSymbol = fromSymbol;
          this.toSymbol = toSymbol;
          this.sourceExplorer = sourceExplorer;
          this.targetExplorer = targetExplorer;
          if (Array.isArray(dexs) && dexs.length) {
            this.dexs = _.uniqWith(dexs, _.isEqual);
          }

          this.isCCTP = this.dexs.some((d) => d.dex == 'CCTP');
          if (this.isCCTP) {
            this.getCCTPReceiveData(
              'CCTP',
              chainIdMainnetMap[this.fromChain],
              chainIdMainnetMap[this.toChain],
              this.sourceHash
            );
          }

          this.timestampNum = timestamp;
          if (timestamp) {
            this.timestamp = dayjs
              .utc(timestamp * 1000)
              .format('MMM-DD-YYYY HH:mm:ss A');
          }
          this.beforeTime = this.beforeTimeFunc(timestamp);
          if (fromDecimals) {
            this.amountIn = amountIn / Math.pow(10, fromDecimals);
          }
          if (toDecimals) {
            this.amountOut = amountOut / Math.pow(10, toDecimals);
          }
          if (statusNotesMap[statusNotes]) {
            this.statusNotes = statusNotes;
          }
          this.isError = false;
        } else {
          this.isError = true;
        }
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.isError = true;
        // console.log('error', error)
      }
    },
    formatTime(seconds) {
      const days = Math.floor(seconds / (60 * 60 * 24));
      seconds -= days * (60 * 60 * 24);

      const hours = Math.floor(seconds / (60 * 60));
      seconds -= hours * (60 * 60);

      const minutes = Math.floor(seconds / 60);
      seconds -= minutes * 60;

      if (this.$i18n.loace === 'zh') {
        return `${days ? days + '天' : ''}${hours ? hours + '小时' : ''}${
          minutes ? minutes + '分钟' : ''
        }${seconds ? seconds + '秒' : ''}`;
      } else {
        const formattedTime = `${
          days ? days + ' ' : ''
        }${hours}:${minutes}:${seconds.toString().padStart(2, '0')}`;

        return formattedTime;
      }
    },
    beforeTimeFunc(timestamp) {
      const date1 = dayjs(Date.now());
      // console.log('month', date1.diff(timestamp * 1000, 'month'))
      let second = date1.diff(timestamp * 1000, 'second');
      let minute = date1.diff(timestamp * 1000, 'minute');
      let hour = date1.diff(timestamp * 1000, 'hour');
      let day = date1.diff(timestamp * 1000, 'day');
      // console.log('day', day)
      // console.log('hour', hour)
      // console.log('minute', minute)
      // console.log('second', second)
      if (second < 60) {
        // 少于60秒
        return second + ' ' + this.$t('secs') + ' ' + this.$t('ago');
      } else if (second >= 60 && second < 60 * 60) {
        // 大于60秒小于1小时
        if (minute > 1) {
          return minute + ' ' + this.$t('mins') + ' ' + this.$t('ago');
        } else {
          return minute + ' ' + this.$t('min') + ' ' + this.$t('ago');
        }
      } else if (second >= 60 * 60 && second < 60 * 60 * 24) {
        // 大于60分钟小于24小时
        if (hour > 1) {
          return hour + ' ' + this.$t('hrs') + ' ' + this.$t('ago');
        } else {
          return hour + ' ' + this.$t('hr') + ' ' + this.$t('ago');
        }
      } else if (second >= 60 * 60 * 24) {
        hour = hour % 24;
        let dayStr = '';
        let hourStr = '';
        if (day > 1) {
          dayStr = this.$t('days');
        } else {
          dayStr = this.$t('day');
        }
        if (hour > 1) {
          hourStr = this.$t('hrs');
          return (
            day +
            ' ' +
            dayStr +
            ' ' +
            hour +
            ' ' +
            hourStr +
            ' ' +
            this.$t('ago')
          );
        } else {
          return day + ' ' + dayStr + ' ' + this.$t('ago');
        }
      } else {
        return '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes rotate {
  0% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(180deg);
  }
}
.hash {
  width: 9rem;
  margin: 0 auto;
  .search-input {
    margin-top: 0.34rem;
    height: 0.48rem;
    padding: 0 0.1rem 0 0.2rem;
    border-width: 0.01rem;
    border-style: solid;
    border-radius: 0.16rem;
    input {
      border: none;
      outline: none;
      font-size: 0.16rem;
      font-family: PingFangSC, PingFangSC-Regular;
      font-weight: 400;
    }
    .delete {
      cursor: pointer;
      width: 0.2rem;
      height: 0.2rem;
      margin: 0 0.2rem;
    }
    .button {
      width: 0.36rem;
      height: 0.36rem;
      border-radius: 0.12rem;
      cursor: pointer;
      img {
        width: 0.18rem;
        height: 0.18rem;
      }
      .loading {
        animation: rotate 1s linear infinite;
      }
    }
  }
  .result-content {
    margin-top: 0.16rem;
    // margin: 0.16rem 0 0.54rem;
    border-radius: 0.16rem;
    box-shadow: 0 0.02rem 0.6rem 0 rgba(0, 0, 0, 0.06);
    .title {
      padding-left: 0.2rem;
      height: 0.54rem;
      line-height: 0.54rem;
      border-bottom-width: 0.01rem;
      border-bottom-style: solid;
    }
    .row {
      margin: 0.2rem 0.2rem 0 0.2rem;
      display: flex;
      align-items: center;
      .name {
        width: 2rem;
      }
      .value {
        // flex: 1;
        word-break: break-word;
      }
      .status-value {
        padding: 0.07rem 0.08rem;
        border-radius: 0.04rem;
        flex: none;
      }
      .right-content {
        .send-value {
          cursor: text;
        }
        .receive-value {
          cursor: text;
        }
      }
      .wallet-content {
        margin-left: 0.2rem;
        display: flex;
        align-items: center;
        .button {
          padding: 0.04rem 0.14rem;
          background: #2980fe;
          border-radius: 0.04rem;
          font-size: 0.12rem;
          cursor: pointer;
          &.gray {
            background: var(--bg-color-3);
            color: var(--text-gray-2);
          }
        }
        .receive-btn {
          font-family: PingFang SC, PingFang SC-Medium;
          font-weight: 500;
          color: #ffffff;
        }
        .wallet-btn {
          margin-left: 0.1rem;
          font-family: PingFang SC, PingFang SC-Regular;
          font-weight: 400;
          color: #ffffff;
        }
        .wallet-pc-btn {
          display: block;
        }
        .wallet-mobile-btn {
          display: none;
        }
      }
      .receive-value {
        font-size: 0.12rem;
      }
      .status-img {
        width: 0.16rem;
        height: 0.16rem;
        margin-right: 0.06rem;
      }
      .copy-wrap {
        display: flex;
        align-items: center;
        position: relative;
        .copy-text {
          display: none;
          position: absolute;
          top: -0.36rem;
          left: 65%;
          transform: translateX(-50%);
          padding: 0.06rem 0.13rem;
          white-space: nowrap;
          background: var(--toast-bg);
          border-radius: 0.04rem;
          color: var(--toast-color);
          font-size: 0.12rem;
          font-family: PingFangSC, PingFangSC-Regular;
        }
      }
      .copy-wrap:hover {
        .copy-text {
          display: block;
        }
      }
      .copy-img {
        width: 0.18rem;
        height: 0.18rem;
        margin-left: 0.1rem;
        cursor: pointer;
      }
      .copy-img-m {
        display: none;
      }
      .time {
        width: 0.12rem;
        height: 0.12rem;
        margin-right: 0.06rem;
      }

      .dexs {
        line-height: 0.3rem;
        &.more-dex {
          margin-right: 0.3rem;
        }
        img {
          width: 0.16rem;
          height: 0.16rem;
          border-radius: 50%;
        }
        span {
          margin-left: 0.06rem;
        }
      }
    }
    .dexs-wrap {
      margin: 0.13rem 0.2rem 0 0.2rem;
      padding-bottom: 0.2rem;
      .question {
        line-height: 0.3rem;
        // margin-left: 0.2rem;
        font-size: 0.11rem;
        font-family: PingFangSC, PingFangSC-Regular;
        font-weight: 400;
        color: var(--pending-color);
        cursor: pointer;
        &.isDexs {
          margin-left: 0;
        }
      }
    }
  }
  .error-content {
    padding: 1.47rem 0 2.12rem;
    img {
      width: 1.49rem;
      height: 1rem;
    }
    span {
      margin-top: 0.3rem;
      font-family: PingFangSC, PingFangSC-Regular;
      font-weight: 400;
    }
  }
}
@media screen and (max-width: 767px) {
  .hash {
    width: 100%;
    padding: 0 0.32rem 0.65rem;
    .search-input {
      margin-top: 0.4rem;
      height: 0.88rem;
      padding: 0 0.1rem 0 0.3rem;
      input {
        font-size: 0.24rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .delete {
        width: 0.32rem;
        height: 0.32rem;
        margin: 0 0.3rem;
      }
      .button {
        width: 0.68rem;
        height: 0.68rem;
        border-radius: 0.16rem;
        img {
          width: 0.28rem;
          height: 0.28rem;
        }
      }
    }
    .result-content {
      margin-top: 0.2rem;
      .title {
        font-size: 0.28rem;
        padding-left: 0.3rem;
        height: 0.9rem;
        line-height: 0.9rem;
      }
      .row {
        margin: 0.3rem 0.3rem 0 0.3rem;
        justify-content: space-between;
        .name {
          width: 2.2rem;
          font-size: 0.24rem;
        }
        .value {
          font-size: 0.26rem;
          text-align: right;
          justify-content: flex-end;
          flex-wrap: wrap;
        }
        .wallet-content {
          margin-left: 0;
          margin-top: 0.2rem;
          .button {
            padding: 0.1rem 0.2rem;
            font-size: 0.22rem;
          }
          .wallet-pc-btn {
            display: none;
          }
          .wallet-mobile-btn {
            margin-left: 0.2rem;
            display: block;
          }
        }
        .date-value {
          flex-direction: column;
          align-items: flex-end;
          .before-time {
            margin-bottom: 0.08rem;
          }
        }
        .status-value {
          padding: 0.08rem 0.12rem;
        }
        .receive-value {
          font-size: 0.26rem;
        }
        .status-img {
          width: 0.26rem;
          height: 0.26rem;
          margin-right: 0.1rem;
        }
        .copy-wrap {
          display: none;
        }
        .copy-img {
          display: none;
        }
        .copy-img-m {
          width: 0.28rem;
          height: 0.28rem;
          display: block;
          margin-left: 0.2rem;
        }
        .time {
          width: 0.24rem;
          height: 0.24rem;
          margin-right: 0.1rem;
        }
        .dexs {
          line-height: 0.5rem;
          // &:nth-child(n + 3) {
          //   margin-top: 0.1rem;
          // }
          &.more-dex {
            margin-right: 0;
          }
          margin-right: 0;
          margin-left: 0.2rem;
          img {
            width: 0.32rem;
            height: 0.32rem;
          }
          span {
            font-size: 0.24rem;
          }
        }
      }
      .dexs-wrap {
        margin: 0.2rem 0.3rem 0 0.3rem;
        .question {
          margin-left: 0.2rem;
          line-height: 0.5rem;
          font-size: 0.2rem;
        }
      }
      .column {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        .right-content {
          width: 100%;
          margin-top: 0.1rem;
          justify-content: space-between;
          span {
            width: 100%;
            padding: 0.1rem 0.2rem;
            background: var(--box-bg-1);
            border-radius: 0.04rem;
            text-align: left;
            font-size: 0.22rem;
            flex: 1;
            word-break: break-word;
          }
          .send-value {
            background: none;
            padding: 0;
            font-size: 0.26rem;
            line-height: 0.37rem;
          }
          .receive-value {
            width: auto;
            max-width: fit-content;
            background: var(--success-bg);
            font-size: 0.26rem;
          }
        }
      }
    }
    .error-content {
      padding: 2.47rem 0 5.32rem;
      img {
        width: 3.02rem;
        height: 2.02rem;
      }
      span {
        margin: 0 0.5rem;
        margin-top: 0.4rem;
        text-align: center;
        font-size: 0.26rem;
      }
    }
  }
}
</style>
